import { Sparkles, Lock, Layers, Zap, Building2, RefreshCw, ShieldCheck, HeartHandshake, Mail } from "lucide-react";
import PublicPageHeader from "@/components/PublicPageHeader";
import SiteFooter from "@/components/SiteFooter";

const WHAT_WE_DO = [
  "Manage patient records securely",
  "Schedule appointments efficiently",
  "Store and organize medical reports",
  "Generate AI-powered clinical insights",
  "Track patient history and health trends",
  "Assist with consultations and digital prescriptions",
];

const WHY_CHOOSE_US = [
  { icon: Sparkles, text: "AI-powered clinical assistance" },
  { icon: Lock, text: "Secure cloud-based platform" },
  { icon: Layers, text: "Simple and intuitive interface" },
  { icon: Zap, text: "Fast and efficient patient management" },
  { icon: Building2, text: "Built specifically for modern clinics" },
  { icon: RefreshCw, text: "Continuous innovation and improvements" },
];

const COMMITMENTS = [
  "Protecting patient privacy and data",
  "Delivering reliable and secure software",
  "Continuously improving our AI capabilities",
  "Supporting healthcare professionals with innovative solutions",
];

export default function AboutUsPage() {
  return (
    <div className="bg-slate-50 text-slate-900">
      <PublicPageHeader />
      <main className="mx-auto max-w-3xl px-4 py-14 sm:px-6" data-testid="about-us-page">
        <h1 className="text-3xl font-semibold text-slate-900 sm:text-4xl">About Us</h1>
        <p className="mt-6 text-sm leading-relaxed text-slate-600 sm:text-base">
          Welcome to RootLyst — an AI-powered Clinic Management Platform designed to simplify clinical workflows and
          help healthcare professionals deliver better patient care.
        </p>

        <section className="mt-10">
          <h2 className="text-lg font-semibold text-slate-900">Our Mission</h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-base">
            Our mission is to empower clinics with intelligent technology that reduces administrative workload,
            improves patient management, and enables doctors to make faster, more informed clinical decisions.
          </p>
        </section>

        <section className="mt-10">
          <h2 className="text-lg font-semibold text-slate-900">What We Do</h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-base">
            RootLyst combines modern clinic management with Artificial Intelligence to help healthcare professionals:
          </p>
          <ul className="mt-3 list-inside list-disc space-y-1.5 text-sm text-slate-600 sm:text-base">
            {WHAT_WE_DO.map((item) => <li key={item}>{item}</li>)}
          </ul>
          <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">
            Our platform is designed to save time, improve efficiency, and enhance the overall patient care experience.
          </p>
        </section>

        <section className="mt-10">
          <h2 className="text-lg font-semibold text-slate-900">Why Choose RootLyst?</h2>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {WHY_CHOOSE_US.map((w) => (
              <div key={w.text} data-testid={`about-why-choose-${w.text.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="card-surface card-surface-hover flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-50 text-teal-700">
                  <w.icon className="h-4 w-4" />
                </div>
                <span className="text-sm font-medium text-slate-800">{w.text}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="text-lg font-semibold text-slate-900">Our Vision</h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-base">
            We envision a future where technology seamlessly supports healthcare professionals, allowing them to
            focus more on patient care and less on administrative tasks.
          </p>
        </section>

        <section className="mt-10">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
            <ShieldCheck className="h-4 w-4 text-teal-700" /> Our Commitment
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-base">At RootLyst, we are committed to:</p>
          <ul className="mt-3 list-inside list-disc space-y-1.5 text-sm text-slate-600 sm:text-base">
            {COMMITMENTS.map((c) => <li key={c}>{c}</li>)}
          </ul>
        </section>

        <section className="mt-10 rounded-xl border border-teal-100 bg-teal-50/60 p-6" data-testid="about-us-contact">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
            <HeartHandshake className="h-4 w-4 text-teal-700" /> Contact Us
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-600 sm:text-base">Have questions or feedback?</p>
          <a href="mailto:contact@rootlyst.com" className="mt-2 flex items-center gap-1.5 text-sm font-medium text-teal-700 hover:underline">
            <Mail className="h-4 w-4" /> contact@rootlyst.com
          </a>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
